import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, AlertCircle, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import ExportButtons from '@/components/ExportButtons';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Event type configuration - matches backend event types and date_field values
// Each type has a distinct color for clear visual differentiation
const EVENT_TYPES = {
  composting: { label: 'Composting', color: 'bg-orange-500', hex: '#F97316', textColor: 'text-orange-700' },
  room_cycle: { label: 'Room Tasks', color: 'bg-blue-500', hex: '#3B82F6', textColor: 'text-blue-700' },
  harvest: { label: 'Harvest', color: 'bg-yellow-500', hex: '#EAB308', textColor: 'text-yellow-700' },
  casing_date: { label: 'Casing', color: 'bg-sky-500', hex: '#0EA5E9', textColor: 'text-sky-700' },
  ruffling_date: { label: 'Ruffling', color: 'bg-violet-500', hex: '#8B5CF6', textColor: 'text-violet-700' },
  airing_start_date: { label: 'Airing', color: 'bg-emerald-500', hex: '#10B981', textColor: 'text-emerald-700' },
  flush1_start_date: { label: 'Flush 1 Start', color: 'bg-amber-500', hex: '#F59E0B', textColor: 'text-amber-700' },
  flush2_start_date: { label: 'Flush 2 Start', color: 'bg-rose-500', hex: '#F43F5E', textColor: 'text-rose-700' },
  empty_date: { label: 'Room Empty', color: 'bg-gray-500', hex: '#6B7280', textColor: 'text-gray-700' }
};

// Filter presets for different roles
const ROLE_FILTERS = {
  growing_supervisor: ['room_cycle', 'harvest', 'casing_date', 'ruffling_date', 'airing_start_date', 'flush1_start_date', 'flush2_start_date', 'empty_date'],
  composting_supervisor: ['composting'],
  owner: Object.keys(EVENT_TYPES),
  grower: Object.keys(EVENT_TYPES)
};

const FarmCalendar = ({ roleFilter = null }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Initialize filters based on role
  const getInitialFilters = () => {
    if (roleFilter && ROLE_FILTERS[roleFilter]) {
      return new Set(ROLE_FILTERS[roleFilter]);
    }
    return new Set(Object.keys(EVENT_TYPES));
  };
  
  const [activeFilters, setActiveFilters] = useState(getInitialFilters);
  const [showFilters, setShowFilters] = useState(false);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/calendar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data.events);
    } catch (error) {
      toast.error('Failed to fetch calendar data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad the beginning to start on Sunday
  const startDay = monthStart.getDay();
  const paddedDays = Array(startDay).fill(null).concat(daysInMonth);

  // Normalize event type for filtering - use date_field for room_cycle events, or fall back to type
  const normalizeEventType = (event) => {
    if (event.type === 'room_cycle') {
      return event.date_field || event.type;
    }
    return event.type;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    return events.filter(event => {
      const eventDate = new Date(event.date);
      const normalizedType = normalizeEventType(event);
      // Also check the main type for general category filtering
      return isSameDay(eventDate, date) && (activeFilters.has(normalizedType) || activeFilters.has(event.type));
    });
  };

  const toggleFilter = (type) => {
    setActiveFilters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  const selectAllFilters = () => setActiveFilters(new Set(Object.keys(EVENT_TYPES)));
  const clearAllFilters = () => setActiveFilters(new Set());

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  const getEventColor = (event) => {
    const normalized = normalizeEventType(event);
    return EVENT_TYPES[normalized]?.hex || event.color || '#9CA3AF';
  };

  const getEventBgClass = (event) => {
    const normalized = normalizeEventType(event);
    return EVENT_TYPES[normalized]?.color || 'bg-gray-400';
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading calendar...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-[#1A4D2E]" />
              Farm Calendar
            </CardTitle>
            <div className="flex items-center gap-2">
              {/* Export Button */}
              <ExportButtons type="calendar" />
              
              {/* Filter Toggle */}
              <Button
                variant={showFilters ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'bg-[#1A4D2E]' : ''}
              >
                <Filter className="w-4 h-4 mr-1" />
                Filters ({activeFilters.size})
              </Button>
              
              {/* Month Navigation */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-lg font-semibold min-w-[140px] text-center">
                {format(currentMonth, 'MMMM yyyy')}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters Panel */}
          {showFilters && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border" data-testid="calendar-filters">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">Filter by Event Type</span>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={selectAllFilters}>Select All</Button>
                  <Button variant="ghost" size="sm" onClick={clearAllFilters}>Clear All</Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(EVENT_TYPES).map(([key, config]) => (
                  <Button
                    key={key}
                    variant={activeFilters.has(key) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleFilter(key)}
                    className={activeFilters.has(key) ? config.color + ' text-white border-0' : ''}
                    data-testid={`filter-${key}`}
                  >
                    {config.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="flex flex-wrap gap-3 mb-4 pb-4 border-b">
            {Object.entries(EVENT_TYPES).map(([key, config]) => (
              <div 
                key={key} 
                className={`flex items-center gap-1 ${!activeFilters.has(key) ? 'opacity-30' : ''}`}
              >
                <div className={`w-3 h-3 rounded ${config.color}`} />
                <span className="text-xs text-gray-600">{config.label}</span>
              </div>
            ))}
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
            {paddedDays.map((day, idx) => {
              if (!day) {
                return <div key={`empty-${idx}`} className="h-24 bg-gray-50 rounded" />;
              }

              const dayEvents = getEventsForDate(day);
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const isToday = isSameDay(day, new Date());
              const hasDelays = dayEvents.some(e => e.has_delay);

              return (
                <div
                  key={day.toISOString()}
                  onClick={() => setSelectedDate(day)}
                  className={`h-24 p-1 rounded border cursor-pointer transition-all overflow-hidden ${
                    isSelected ? 'border-[#1A4D2E] border-2 bg-green-50' :
                    isToday ? 'border-blue-300 bg-blue-50' :
                    'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${
                      isToday ? 'text-blue-600' : 'text-gray-700'
                    }`}>
                      {format(day, 'd')}
                    </span>
                    {hasDelays && <AlertCircle className="w-3 h-3 text-red-500" />}
                  </div>
                  <div className="mt-1 space-y-0.5 overflow-hidden">
                    {dayEvents.slice(0, 3).map((event, i) => (
                      <div
                        key={i}
                        className={`text-[10px] px-1 py-0.5 rounded truncate text-white ${getEventBgClass(event)}`}
                        title={event.title}
                      >
                        {event.title.length > 15 ? event.title.substring(0, 15) + '...' : event.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-gray-500 text-center">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Selected Date Events */}
      {selectedDate && (
        <Card data-testid="selected-date-events">
          <CardHeader>
            <CardTitle className="text-base">
              Events on {format(selectedDate, 'PPPP')} ({selectedDateEvents.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDateEvents.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No events scheduled for this date (check filters)</p>
            ) : (
              <div className="space-y-3">
                {selectedDateEvents.map((event, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg border-l-4 bg-gray-50 ${
                      event.has_delay ? 'border-red-500' : ''
                    }`}
                    style={{ borderLeftColor: getEventColor(event) }}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{event.title}</p>
                        {event.room_name && (
                          <p className="text-sm text-gray-600">Room: {event.room_name}</p>
                        )}
                        {event.batch_name && (
                          <p className="text-sm text-gray-500">Batch: {event.batch_name}</p>
                        )}
                        {event.flush_day && (
                          <p className="text-xs text-gray-400">Harvest Day {event.flush_day}/4</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {event.has_delay && (
                          <Badge className="bg-red-100 text-red-800">Delayed</Badge>
                        )}
                        <Badge
                          className="text-white text-xs"
                          style={{ backgroundColor: getEventColor(event) }}
                        >
                          {EVENT_TYPES[normalizeEventType(event)]?.label || event.task || event.type}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FarmCalendar;
